/**
 * StatefulMacroCoordinator — host-side macro tree walk orchestration.
 */

import {
  generateMacroMessage,
  MacroMessageType,
  regionKey,
  type MacroCaptureResult,
  type MacroMessage,
  type MacroTrigger,
  type MacroWalkResponsePayload,
  type WebsocketMessage,
} from '@playcastdotio/Messaging';
import type { IRealtimeService } from '../../services/realtime/IRealtimeService';
import type { IPlayjectorService, MacroStepCaptureResult } from '../../services/playjector/interfaces/IPlayjectorService';
import type { IMessageRouter } from '../../services/MessageRouter';
import { MacroManager } from '../game-launch/managers/MacroManager';
import type { GameLaunchCoordinator } from '../game-launch/GameLaunchCoordinator';
import type { PavilionCoordinator } from '../pavilion/PavilionCoordinator';
import { machineIdState } from '../../api/stores/shared';
import { createStatefulMacroWalkStore, type StatefulMacroWalkState } from './stores';
import { extractWalkResponsePayload, generateLaunchInstanceId } from './helpers';
import { GuestFrameTracker } from './managers/GuestFrameTracker';
import { MacroWaitPollManager } from './managers/MacroWaitPollManager';
import {
  MacroWalkRecoveryManager,
  MACRO_WALK_MAX_RETRIES,
} from './managers/MacroWalkRecoveryManager';
import { MacroWalkStepExecutor } from './managers/MacroWalkStepExecutor';
import * as Sentry from '@sentry/react';

export { MACRO_WALK_MAX_RETRIES };
export const DISABLED_REASON = 'stateful_macro_system_disabled';

function waitMs(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export interface StatefulMacroCoordinatorDeps {
  realtimeService: IRealtimeService;
  playjectorService: IPlayjectorService;
  macroManager: MacroManager;
  gameLaunchCoordinator: GameLaunchCoordinator;
  pavilionCoordinator: PavilionCoordinator;
}

interface InFlightWalk {
  lastRequest: MacroMessage;
}

export class StatefulMacroCoordinator {
  private readonly realtimeService: IRealtimeService;
  private readonly playjectorService: IPlayjectorService;
  private readonly macroManager: MacroManager;
  private readonly gameLaunchCoordinator: GameLaunchCoordinator;
  private readonly pavilionCoordinator: PavilionCoordinator;

  private readonly walkStore = createStatefulMacroWalkStore();
  private readonly guestFrameTracker = new GuestFrameTracker();
  private readonly waitPollManager = new MacroWaitPollManager();
  private readonly recoveryManager: MacroWalkRecoveryManager;
  private readonly stepExecutor: MacroWalkStepExecutor;

  private _isWaitingOutSettle = false;
  private inFlight: InFlightWalk | null = null;
  private processingWalkResponse = false;
  private responseGeneration = 0;
  private initialized = false;
  private readonly disposers: Array<() => void> = [];

  constructor(deps: StatefulMacroCoordinatorDeps) {
    this.realtimeService = deps.realtimeService;
    this.playjectorService = deps.playjectorService;
    this.macroManager = deps.macroManager;
    this.gameLaunchCoordinator = deps.gameLaunchCoordinator;
    this.pavilionCoordinator = deps.pavilionCoordinator;

    this.recoveryManager = new MacroWalkRecoveryManager({
      gameLaunchCoordinator: this.gameLaunchCoordinator,
      pavilionCoordinator: this.pavilionCoordinator,
      deactivateWalk: () => this.deactivateWalk(),
    });

    this.stepExecutor = new MacroWalkStepExecutor({
      macroManager: this.macroManager,
      playjectorService: this.playjectorService,
      guestFrameTracker: this.guestFrameTracker,
      getLaunchInstanceId: () => this.walkStore.currentState.launchInstanceId,
      isCurrentLaunch: (id) => this.isCurrentLaunch(id),
      sendWalkRequest: (args) => this.sendWalkRequest(args),
      updateCurrentNode: (nextNodeId) => {
        this.walkStore.updateState({ ...this.walkStore.currentState, currentNodeId: nextNodeId });
      },
      fallbackToLegacy: (reason) => this.fallbackToLegacy(reason),
      setSettling: (active) => { this._isWaitingOutSettle = active; },
    });
  }

  public init(): void {
    if (this.initialized) {
      console.log('[StatefulMacroSystemStep] init: already initialized — no-op');
      return;
    }
    this.initialized = true;

    this.disposers.push(
      this.gameLaunchCoordinator.events.gameLaunched.addListener(() => this.handleGameLaunched()),
      this.realtimeService.onConnected(() => this.handleRealtimeReconnect()),
    );

    console.log('[StatefulMacroSystemStep] ✅ Initialized');
  }

  public registerMessageHandlers(router: IMessageRouter): void {
    router.register(MacroMessageType.MACRO_WALK_RESPONSE, (msg: WebsocketMessage) => {
      if (msg.type !== MacroMessageType.MACRO_WALK_RESPONSE) return;
      void this.handleMacroWalkResponse(msg as MacroMessage);
    });
  }

  public get state(): StatefulMacroWalkState {
    return {
      ...this.walkStore.currentState,
      retryCount: this.recoveryManager.retryCount,
    };
  }

  public isWaitingOutSettle(): boolean {
    return this._isWaitingOutSettle || this.waitPollManager.isWaiting();
  }

  public destroy(): void {
    for (const d of this.disposers) {
      try { d(); } catch (err) { console.error('[StatefulMacroSystemStep] Error in disposer', err); }
    }
    this.disposers.length = 0;
    this.recoveryManager.clearRequeue();
    this.initialized = false;
    this.inFlight = null;
    this.processingWalkResponse = false;
    this.responseGeneration += 1;
    this.playjectorService.abandonMacroStep();
  }

  private handleGameLaunched(): void {
    const cfg = this.pavilionCoordinator.getCampaignConfig();
    if (!cfg?.useStatefulMacroSystem) return;

    const campaignId = cfg.campaignId;
    if (!campaignId) {
      console.warn('[StatefulMacroSystemStep] gameLaunched with stateful macros but no campaignId — bailing to legacy');
      void this.gameLaunchCoordinator.executeLegacyOnLaunchMacro();
      return;
    }

    const playjectorState = this.playjectorService.getCurrentState();
    if (!playjectorState.gameLaunched) {
      this.recoveryManager.armGameLaunchedRequeue(
        campaignId,
        (cb) => this.playjectorService.onStateChange((state) => cb(state.gameLaunched)),
        () => this.handleGameLaunched(),
      );
      return;
    }

    this.recoveryManager.clearRequeue();

    if (this.recoveryManager.retryCampaignId !== campaignId) {
      this.recoveryManager.resetRetries();
      this.recoveryManager.retryCampaignId = campaignId;
    }

    this.deactivateWalk();

    const trigger: MacroTrigger = 'launch';
    const launchInstanceId = generateLaunchInstanceId();

    this.walkStore.updateState({
      active: true,
      trigger,
      currentNodeId: null,
      retryCount: this.recoveryManager.retryCount,
      launchInstanceId,
      waitNodeId: null,
      waitDeadlineMs: null,
    });

    console.log('[StatefulMacroSystemStep] 🚀 Starting macro walk', {
      campaignId,
      trigger,
      launchInstanceId,
      retryCount: this.recoveryManager.retryCount,
    });

    this.sendWalkRequest({
      campaignId,
      trigger,
      launchInstanceId,
      currentNodeId: null,
      captures: [],
    });
  }

  private async handleMacroWalkResponse(message: MacroMessage): Promise<void> {
    if (message.type !== MacroMessageType.MACRO_WALK_RESPONSE) return;

    const payload = extractWalkResponsePayload(message);

    console.log('[StatefulMacroSystemStep] 📥 MACRO_WALK_RESPONSE received', {
      outcome: payload.outcome,
      nextNodeId: payload.nextNodeId,
      captureCount: payload.captures.length,
      macroCount: payload.macros.length,
      launchInstanceId: payload.launchInstanceId,
    });

    const expected = this.walkStore.currentState.launchInstanceId;
    if (!expected || payload.launchInstanceId !== expected || !this.walkStore.currentState.active) {
      console.warn('[StatefulMacroSystemStep] Dropping stale/inactive MACRO_WALK_RESPONSE');
      return;
    }

    if (this.processingWalkResponse) {
      console.warn('[StatefulMacroSystemStep] Dropping concurrent MACRO_WALK_RESPONSE', {
        launchInstanceId: payload.launchInstanceId,
        outcome: payload.outcome,
        hasWaitPoll: Boolean(payload.waitPoll),
      });
      return;
    }

    const generation = ++this.responseGeneration;
    this.processingWalkResponse = true;
    this.inFlight = null;
    try {
      switch (payload.outcome) {
        case 'continue':
          if (payload.waitPoll) {
            await this.handleWaitContinueOutcome(payload);
          } else {
            this.clearWaitState();
            await this.stepExecutor.executeContinue(payload);
          }
          return;
        case 'complete':
          await this.handleCompleteOutcome(payload);
          return;
        case 'failure':
          await this.handleFailureOutcome(payload);
          return;
        default: {
          const exhaustive: never = payload.outcome;
          console.error('[StatefulMacroSystemStep] Unhandled outcome', exhaustive);
        }
      }
    } finally {
      if (generation === this.responseGeneration) {
        this.processingWalkResponse = false;
      }
    }
  }

  private async handleWaitContinueOutcome(payload: MacroWalkResponsePayload): Promise<void> {
    const nodeId = payload.nextNodeId;
    const waitPoll = payload.waitPoll;
    if (!nodeId || !waitPoll) {
      this.fallbackToLegacy('invalid_wait_poll_payload');
      return;
    }

    const state = this.walkStore.currentState;
    const firstEnter = state.waitNodeId !== nodeId || state.waitDeadlineMs === null;
    if (firstEnter) {
      this.waitPollManager.beginWaitNode(nodeId, waitPoll.timeoutMs);
      this.syncWaitStoreFromPollManager();
    }

    try {
      await this.stepExecutor.runWalkMacros(payload, 'StatefulMacro:wait');
    } catch (err) {
      console.error('[StatefulMacroSystemStep] runMacroSteps threw on Wait path:', err);
      this.fallbackToLegacy('wait_runMacroSteps_threw');
      return;
    }

    if (!this.isCurrentLaunch(payload.launchInstanceId)) return;

    if (!firstEnter) {
      const deadlineMs = this.walkStore.currentState.waitDeadlineMs;
      if (deadlineMs !== null && Date.now() >= deadlineMs) {
        await this.restartWait(payload, 'wait_timeout');
        return;
      }
      await waitMs(waitPoll.intervalMs);
      if (!this.isCurrentLaunch(payload.launchInstanceId)) return;
    }

    await this.waitPollManager.pollUntilRequestOrTerminal(
      {
        nodeId,
        launchInstanceId: payload.launchInstanceId,
        nextNodeId: nodeId,
        waitPoll,
        captures: payload.captures,
        buildCapturesFromHashes: (hashes) => this.buildCapturesFromHashes(hashes, payload),
      },
      {
        isCurrentLaunch: (id) => this.isCurrentLaunch(id),
        onRestart: (reason) => this.restartWait(payload, reason),
        onLegacyFallback: (reason) => {
          // Wait-capture failure mid-wait must not silently degrade to the
          // legacy macro — surface the real reason through the campaign-switch
          // failure channel.
          this.fallbackToLegacy(reason, {
            reportCampaignSwitchFailure: true,
            campaignId: payload.campaignId,
          });
        },
        requestCapture: () => this.playjectorService.requestMacroStep(
          this.guestFrameTracker.buildCaptureSpecs(payload),
        ),
        sendWalkRequest: (captures, waitMatchCurrent) => {
          this.guestFrameTracker.rememberGuestFrameFromHashes(
            Object.fromEntries(captures.map((c) => [c.captureId, { hash: c.hash, width: c.width, height: c.height }])),
          );
          this.sendWalkRequest({
            campaignId: payload.campaignId,
            trigger: payload.trigger,
            launchInstanceId: payload.launchInstanceId,
            currentNodeId: nodeId,
            captures,
            waitMatchCurrent,
          });
        },
        updateWalkNode: (nextNodeId) => {
          this.walkStore.updateState({ ...this.walkStore.currentState, currentNodeId: nextNodeId });
        },
      },
    );
  }

  private buildCapturesFromHashes(
    hashes: Record<string, MacroStepCaptureResult>,
    payload: MacroWalkResponsePayload,
  ): MacroCaptureResult[] {
    const specs = this.guestFrameTracker.buildCaptureSpecs(payload);
    this.guestFrameTracker.rememberGuestFrameFromHashes(hashes);
    return Object.entries(hashes).map(([captureId, result]) => {
      const spec = specs[captureId];
      return {
        captureId,
        hash: result.hash,
        width: result.width,
        height: result.height,
        ...(spec && regionKey(spec.region) !== '' ? { region: spec.region } : {}),
      };
    });
  }

  private async handleCompleteOutcome(payload: MacroWalkResponsePayload): Promise<void> {
    await this.stepExecutor.executeComplete(payload);

    if (!this.isCurrentLaunch(payload.launchInstanceId)) return;

    const shouldSignalLaunchComplete =
      (payload.trigger ?? this.walkStore.currentState.trigger) === 'launch';

    this.deactivateWalk();
    this.recoveryManager.resetRetries();

    if (shouldSignalLaunchComplete) {
      this.gameLaunchCoordinator.signalLaunchMacroWalkComplete();
    }
  }

  private async handleFailureOutcome(payload: MacroWalkResponsePayload): Promise<void> {
    if (payload.onFailure === 'restart') {
      await this.recoveryManager.handleRestartFailure(payload);
      return;
    }

    if (payload.reason === DISABLED_REASON) {
      console.log('[StatefulMacroSystemStep] 🟡 disabled — falling back to legacy onLaunchMacro');
    } else {
      console.error('[StatefulMacroSystemStep] 🔴 error — falling back to legacy onLaunchMacro', {
        reason: payload.reason ?? null,
      });
    }
    this.fallbackToLegacy(payload.reason ?? 'walk_failure');
  }

  private async restartWait(payload: MacroWalkResponsePayload, reason: string): Promise<void> {
    await this.recoveryManager.handleRestartFailure({
      ...payload,
      outcome: 'failure',
      nextNodeId: null,
      macros: [],
      captures: [],
      onFailure: 'restart',
      reason,
      waitPoll: undefined,
    });
  }

  private handleRealtimeReconnect(): void {
    if (!this.inFlight || !this.walkStore.currentState.active) return;
    console.log('[StatefulMacroSystemStep] 🌐 Realtime reconnected — replaying in-flight MACRO_WALK_REQUEST');
    this.safeSend(this.inFlight.lastRequest, 'reconnect_replay_send_failed');
  }

  private sendWalkRequest(args: {
    campaignId: string;
    trigger: MacroTrigger;
    launchInstanceId: string;
    currentNodeId: string | null;
    captures: MacroCaptureResult[];
    waitMatchCurrent?: boolean;
  }): void {
    const machineId = machineIdState.currentState;
    if (!machineId) {
      this.fallbackToLegacy('no_machine_id');
      return;
    }

    const message = generateMacroMessage(MacroMessageType.MACRO_WALK_REQUEST, {
      machineId,
      campaignId: args.campaignId,
      trigger: args.trigger,
      launchInstanceId: args.launchInstanceId,
      currentNodeId: args.currentNodeId,
      captures: args.captures,
      ...(args.waitMatchCurrent === true ? { waitMatchCurrent: true } : {}),
    });

    console.log('[StatefulMacroSystemStep] 📤 Sending MACRO_WALK_REQUEST', {
      campaignId: args.campaignId,
      currentNodeId: args.currentNodeId,
      captureCount: args.captures.length,
      launchInstanceId: args.launchInstanceId,
    });

    this.responseGeneration += 1;
    this.processingWalkResponse = false;
    this.inFlight = { lastRequest: message };
    this.safeSend(message, 'send_failed');
  }

  private safeSend(message: MacroMessage, fallbackReason: string): void {
    try {
      this.realtimeService.sendMessage(message);
    } catch (err) {
      console.error('[StatefulMacroSystemStep] ❌ Failed to send MACRO_WALK_REQUEST — falling back to legacy', {
        fallbackReason,
        error: err instanceof Error ? err.message : String(err),
      });
      this.fallbackToLegacy(fallbackReason);
    }
  }

  private deactivateWalk(): void {
    this.walkStore.updateState({
      ...this.walkStore.currentState,
      active: false,
      trigger: null,
      currentNodeId: null,
      launchInstanceId: null,
      waitNodeId: null,
      waitDeadlineMs: null,
    });
    this.inFlight = null;
    this.processingWalkResponse = false;
    this.responseGeneration += 1;
    this.waitPollManager.clear();
    this.guestFrameTracker.clear();
    this.playjectorService.abandonMacroStep();
  }

  private clearWaitState(): void {
    this.waitPollManager.clear();
    this.syncWaitStoreFromPollManager();
  }

  private syncWaitStoreFromPollManager(): void {
    const pollState = this.waitPollManager.getState();
    this.walkStore.updateState({
      ...this.walkStore.currentState,
      waitNodeId: pollState.waitNodeId,
      waitDeadlineMs: pollState.waitDeadlineMs,
    });
  }

  private isCurrentLaunch(launchInstanceId: string): boolean {
    return this.walkStore.currentState.launchInstanceId === launchInstanceId;
  }

  private fallbackToLegacy(
    reason: string,
    opts: { reportCampaignSwitchFailure?: boolean; campaignId?: string } = {},
  ): void {
    console.log('[StatefulMacroSystemStep] ⤴️  Falling back to legacy onLaunchMacro path:', reason);
    Sentry.logger.warn(`[StatefulMacroSystemStep] Falling back to legacy onLaunchMacro path: ${reason}`);
    if (opts.reportCampaignSwitchFailure && opts.campaignId) {
      // A Wait-node capture failure must not look like a successful walk —
      // surface the real reason through the campaign-switch failure channel
      // so observability can distinguish "screen never appeared" from a clean
      // legacy path.
      this.gameLaunchCoordinator.reportCampaignSwitchFailure(opts.campaignId, reason, false);
    }
    this.deactivateWalk();
    this.recoveryManager.resetRetries();
    void this.gameLaunchCoordinator.executeLegacyOnLaunchMacro();
  }
}
